<template>
  <div>
    <HeroSection
      :title="$c.text('pages.course-certificate.heroTitle')"
      :subtitle="$c.text('pages.course-certificate.heroSubtitle')"
      :button-title="
        $c.text(
          $config.platform === 'ds' ? 'button.start-your-career' : 'button.get-started'
        )
      "
      data-event="start_your_career_1.click"
      button-link="/signup/"
      :image-url="imageUrl"
      :image-alt="imageAlt"
      :mdoffset="1"
      class-name="left-shadow right-shadow certificate-page"
    />
    <div class="col-12">
      <div class="container">
        <div
          class="site-section receive-your-course tracking-section row"
          data-event="receive_course_certificate.scroll"
        >
          <div class="offset-2 col-8 col text-center md-10 md-offset-1">
            <h2 class="h3">
              {{ $c.text("pages.course-certificate.receiveCertificate.title") }}
            </h2>
          </div>
          <div class="offset-1 col-10 col md-10 md-offset-1">
            <div class="row">
              <div v-for="k in 3" :key="k" class="col-3 offset-1">
                <p class="h5 step-title">
                  {{ $c.text(`pages.course-certificate.receiveCertificate.step${k}.title`) }}
                </p>
                <p class="body-1">
                  {{
                    $c.text(`pages.course-certificate.receiveCertificate.step${k}.description`)
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div
          class="site-section share-to-linkedin tracking-section"
          data-event="share_certificate_linkedin.scroll"
        >
          <div class="offset-1 col-10">
            <div class="row">
              <div class="col-5 col sm-8">
                <div class="col-12 col">
                  <h2>
                    {{ $c.text("pages.course-certificate.shareCertificate.title") }}
                  </h2>
                  <div v-if="$store.state.layout === 'mobile'">
                    <Animation :options="{ data: socialMediaAnimation }" />
                  </div>
                  <div class="body-1">
                    <p>{{ $c.text("pages.course-certificate.shareCertificate.subtitle") }}</p>
                    <ButtonArrow
                      text="Start Building Your Career"
                      type="nuxt-link"
                      to="/signup/"
                      class="strong"
                      data-event="start_building_your_career_1.click"
                    />
                  </div>
                </div>
              </div>
              <div class="col-1" />
              <div v-if="$store.state.layout !== 'mobile'" class="col-6 col">
                <Animation :options="{ data: socialMediaAnimation }" />
              </div>
            </div>
          </div>
        </div>
        <div
          class="site-section professional-resume tracking-section"
          data-event="professional_resume.scroll"
        >
          <div class="offset-1 col-10">
            <div class="row">
              <div v-if="$store.state.layout !== 'mobile'" class="col-6">
                <Animation :options="{ data: resumeAnimation }" />
              </div>
              <div class="col-1 col" />
              <div class="col-5 col sm-8">
                <div class="col-12">
                  <h2 class="h2">
                    {{ $c.text("pages.course-certificate.resume.title") }}
                  </h2>
                  <div v-if="$store.state.layout === 'mobile'" class="col-6">
                    <Animation :options="{ data: resumeAnimation }" />
                  </div>
                  <div class="body-1">
                    <p>{{ $c.text("pages.course-certificate.resume.subtitle") }}</p>
                    <ButtonArrow
                      text="Start Building Your Career"
                      type="nuxt-link"
                      to="/signup/"
                      data-event="start_building_your_career_2.click"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="main-light-bg">
      <div class="col-12">
        <div
          v-if="list.upcoming && list.upcoming.length"
          class="upcoming-courses container tracking-section"
          data-event="upcoming_courses.scroll"
        >
          <Slider v-if="list.upcoming.length">
            <h2 slot="heading" class="h3">
              Upcoming {{ $config.platform === 'ds' ? 'Data Science' : '' }} Courses
            </h2>
            <nuxt-link slot="controls" to="/upcoming-courses/">
              Show All
            </nuxt-link>
            <div
              v-for="course in list.upcoming"
              :key="course.id"
              slot="slide"
              class="col col-4 sm-6"
            >
              <nuxt-link
                :to="`/upcoming-courses/#${course.id}`"
                data-event="upcoming_courses.click"
              >
                <div class="upcoming-course">
                  <div class="image">
                    <div class="icon" :class="course.technology">
                      <img
                        :src="require(`@itbpbg/lms-components/src/assets/svg/tech-responsive-${course.technology.replace(' ', '-')}.svg`)"
                        :alt="course.technology === 'theory' ? `icon indicating that the course covers a theoretical tech topic` : `${course.technology}'s logo`"
                      >
                    </div>
                    <img
                      loading="lazy"
                      :src="`${$config.imagesUrl}/upcoming-courses/${course.instructorPhoto}`"
                      :alt="course.instructor"
                    >
                  </div>
                  <div class="info">
                    <h3 class="h5">
                      {{ course.name }}
                    </h3>
                    <div class="instructors">
                      with {{ course.instructor }}
                    </div>
                  </div>
                </div>
              </nuxt-link>
            </div>
          </Slider>
        </div>
      </div>
      <Lazy :when-visible="{ rootMargin: '200px' }">
        <div class="col-12">
          <div
            class="site-section tracking-section"
            :class="{ 'frequently-asked-questions-fa': $config.platform === 'fa' }"
            data-event="faq.scroll"
          >
            <FrequentlyAskedQuestions group="certificates" />
          </div>
        </div>
      </Lazy>
    </div>
    <div>
      <StartLearningToday
        class="start-learn-today tracking-section"
        data-event="start_learning_today.scroll"
      >
        <h2 class="h2">
          {{ $c.text("component.start-learning-today.title") }}
        </h2>
        <p class="body-1">
          {{ $c.text( $config.platform === 'ds' ? 'component.start-learning-today.description' : 'component.start-learning-today.description2') }}
        </p>
        <ul v-if="$config.platform === 'fa'">
          <li class="body-1">
            {{ $c.text("component.start-learning-today.li1") }}
          </li>
          <li class="body-1">
            {{ $c.text("component.start-learning-today.li2") }}
          </li>
          <li class="body-1">
            {{ $c.text("component.start-learning-today.li3") }}
          </li>
          <li class="body-1">
            {{ $c.text("component.start-learning-today.li4") }}
          </li>
        </ul>
        <ul v-else>
          <li class="body-1">
            {{ stats.rounded.courses }}+ data and AI courses
          </li>
          <li class="body-1">
            {{ stats.rounded.exercises }}+ hands-on exercises
          </li>
          <li class="body-1">
            {{ stats.rounded.projects }}+ real data projects
          </li>
          <li class="body-1">
            Certificates of achievement
          </li>
        </ul>
      </StartLearningToday>
    </div>
  </div>
</template>

<script>
import Lazy from 'vue-lazy-hydration';
import global from '@itbpbg/lms-components/src/mixins/global';
import ButtonArrow from '@itbpbg/lms-components/src/components/ButtonArrow';
import Slider from '@itbpbg/lms-components/src/components/Slider';
import HeroSection from '@itbpbg/lms-components/src/components/HeroSection';
import FrequentlyAskedQuestions from '@itbpbg/lms-components/src/FrequentlyAskedQuestions';
import StartLearningToday from './StartLearningToday';
import Animation from './Animation';

export default {
  components: {
    ButtonArrow,
    Slider,
    HeroSection,
    FrequentlyAskedQuestions,
    StartLearningToday,
    Animation,
    Lazy,
  },
  mixins: [global],
  props: {
    list: { type: Object, required: true, default: () => { } },
    page: { type: Object, required: true, default: () => { } },
    stats: { type: Object, required: true, default: () => { } },
  },
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
    ]);

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [{ rel: 'canonical', href: this.canonicalURL() }],
      meta: this.metaProps(this.page),
      script: [linkedData],
    };
  },
  computed: {
    imageUrl() {
      return this.$config.platform === 'fa'
        ? 'fa-course-certificate.jpg'
        : 'course-certificate.jpg';
    },
    imageAlt() {
      return this.$config.platform === 'fa'
        ? 'Darren Wilson’ Certificate of Achievement for passing the exam and successfully completing the Introduction to Business Analytics course.'
        : 'Ronald Burks’ Certificate of Achievement for passing the exam and successfully completing the Introduction to Data and Data Science course.';
    },
    socialMediaAnimation() {
      return this.$config.platform === 'fa'
        ? 'fa/fa-share-your-certificate-on-linkedIn'
        : 'share-your-certificate-on-linkedIn';
    },
    resumeAnimation() {
      return this.$config.platform === 'fa'
        ? 'fa/fa-add-to-your-professional-resume'
        : 'add-to-your-professional-resume';
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  .hero-section-wrapper .container .row > div .subtitle {
    margin-bottom: var(--spacing-48);
  }
}

.receive-your-course {
  padding-top: var(--spacing-100);
  overflow: hidden;
  counter-reset: section;

  .h3 {
    margin-bottom: var(--spacing-80);
  }

  .col-3 {
    position: relative;

    .step-title {
      color: var(--main-dark-shade-0);
    }

    p {
      color: var(--main-dark-shade-3);
      margin-bottom: 0;
    }

    &:before {
      background-color: var(--accent-shade-1);
      border-radius: 50%;
      color: var(--main-light);
      font-size: 18px;
      font-weight: 600;
      line-height: 30px;
      text-align: center;
      counter-increment: section;
      content: counter(section);
      height: 28px;
      position: absolute;
      width: 28px;
      left: -42px;
      top: -21px;
      z-index: 1;
    }

    &:after {
      background-color: var(--main-light-shade-3);
      content: "";
      height: 100%;
      left: -28px;
      position: absolute;
      top: -10px;
      width: 1px;
      z-index: 0;
    }
  }

  @include bp($bp-mobile) {
    .col-3 {
      margin-bottom: var(--spacing-36);
      padding-left: var(--spacing-90);

      &:before {
        left: 30px;
      }

      &:after {
        left: 43px;
      }
    }
  }

  @include bp($bp-tablet) {
    max-width: 450px;
    margin: auto;

    .col-3 {
      margin-bottom: var(--spacing-36);
      padding-left: var(--spacing-90);

      &:before {
        left: 30px;
      }

      &:after {
        left: 43px;
      }
    }
  }
}

.share-to-linkedin,
.professional-resume {
  .h2,
  .h2 + .body-1 p {
    margin-bottom: var(--spacing-36);
  }

  .col-5 {
    display: flex;
    align-items: center;
  }
}

.upcoming-courses {
  color: var(--main-dark-shade-0);
  padding-top: var(--spacing-100);
  padding-bottom: var(--spacing-120);
  position: relative;
  z-index: 2;

  .slider-header {
    .h3 {
      margin: 0;
    }
  }

  a {
    color: var(--main-dark-shade-0);
  }

  .col-4 {
    padding-bottom: 10px;
    padding-top: 10px;
  }

  .controls {
    a {
      color: var(--main-dark-shade-0);
    }
  }

  .upcoming-course {
    background-color: var(--main-light);
    border-radius: 7px;
    box-shadow: 0 3px 8px rgba(0, 12, 31, 0.1);
    overflow: hidden;
    height: 100%;

    .image {
      position: relative;
      height: 220px;

      .icon {
        position: absolute;
        right: 28px;
        top: 20px;
        z-index: 1;

        img {
          max-width: 44px;
          height: 100%;
          width: 100%;
          filter: drop-shadow(0 2px 10px rgba(0, 12, 31, 0.5));
        }
      }

      > img {
        width: 100%;
        height: 100%;
        -o-object-fit: cover;
        object-fit: cover;
        object-position: 100%;
      }
    }

    .info {
      padding: var(--spacing-24);

      .h5 {
        margin-top: 0;
        margin-bottom: var(--spacing-16);
      }

      .instructors {
        color: var(--main-dark-shade-3);
        line-height: 18px;
      }
    }
  }
}

.main-light-bg {
  background-color: var(--main-light-shade-1);
}

.start-learn-today {
  .h2 {
    margin-bottom: var(--spacing-24);
  }

  p.body-1 {
    margin-bottom: var(--spacing-36);
  }
}

@include bp($bp-laptop) {
  ::v-deep {
    .hero-section-wrapper .container .row > div {
      .btn-primary {
        max-width: 225px;
      }
    }
  }

  .receive-your-course {
    .h3 {
      font-size: 32px;
    }
  }
}

@include bp($bp-tablet) {
  .site-section {
    .col-1 {
      display: none;
    }

    .sm-8 {
      margin: auto;
    }
  }
}

@include bp($bp-mobile) {
  .site-section {
    padding: var(--spacing-60) 0;
  }
}

.fa {
  @import "../assets/sass/fa/pages/certificate.scss";
}
</style>
